import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { MailrailAutoSendEmailCampaign, TPredicateMode } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardV2 as Card,
  Label,
  UniversalSelect,
  Well,
} from '@feathr/components';
import PredicateList from '@feathr/extender/components/PredicateList';
import { addPredicate } from '@feathr/extender/components/PredicateList/PredicateList.utils';

import * as styles from './MailrailPredicatesConfig.css';

interface IProps {
  campaign: MailrailAutoSendEmailCampaign;
  disabled: boolean;
  label?: string;
  /** 'card' (default) renders a titled card; 'well' renders a flat white well for the drip-builder panel. */
  variant?: 'card' | 'well';
}

interface IModeOption extends IUniversalSelectOption {
  id: TPredicateMode;
}

function MailrailPredicatesConfig({
  campaign,
  disabled,
  label,
  variant = 'card',
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const predicates = campaign.get('predicates') ?? [];
  const mode = campaign.get('predicates_mode') ?? 'match_any';
  const subtype = campaign.get('subtype');
  const predicateKind = subtype === 'time' ? 'attribute' : subtype;

  const modeOptions: IModeOption[] = [
    { id: 'match_all', name: t('all') },
    { id: 'match_any', name: t('any') },
  ];

  const modeOption = modeOptions.find(({ id }) => id === mode) ?? modeOptions[0];

  function handleAddTrigger(): void {
    addPredicate(
      campaign,
      ensurePredicateKeys({
        attr_against: undefined,
        kind: predicateKind,
      }),
    );
  }

  function handleChangeMode(option: IModeOption): void {
    campaign.set({ predicates_mode: option.id });
  }

  const predicateInputs = (
    <>
      <div className={styles.header}>
        <Trans t={t}>
          Send an email to people that match{' '}
          <UniversalSelect<IModeOption>
            className={styles.mode}
            // Mode is limited to any when date triggered is selected
            disabled={disabled || campaign.get('subtype') === 'time'}
            name={'mailrail-predicates-mode'}
            onSelectSingle={handleChangeMode}
            options={modeOptions}
            value={modeOption}
          />
          of the following filters:
        </Trans>
      </div>
      {predicates.length > 0 && (
        <div>
          <PredicateList
            disabled={disabled}
            glueMode={mode}
            model={campaign}
            optionsGroup={
              subtype === 'time' ? 'emailCampaignDateTrigger' : 'emailCampaignCrumbTrigger'
            }
          />
        </div>
      )}
    </>
  );

  const triggerActions = !disabled && (
    <Button
      name={'add_filter'}
      onClick={handleAddTrigger}
      prefix={<FontAwesomeIcon icon={faPlus} />}
    >
      {t('Add Trigger')}
    </Button>
  );

  return variant === 'card' ? (
    <Card width={'full'}>
      <CardHeader title={label ?? t('Triggers')} />
      <CardContent>{predicateInputs}</CardContent>
      <CardActions contentClassName={styles.predicates}>{triggerActions}</CardActions>
    </Card>
  ) : (
    <Well layout={'vertical'} theme={'white'}>
      <Label margin={'condensed'} weight={'medium'}>
        {label ?? t('Triggers')}
      </Label>
      {predicateInputs}
      {triggerActions}
    </Well>
  );
}

export default observer(MailrailPredicatesConfig);
